import { BADGE, type LiftableAction, type ModerationAction } from "odyssey-shared";
import { say } from "../../words.ts";
import { muteTime } from "../chat/words.ts";

/**
 * Moderation's wording that takes a decision: which sentence a ban, an ended action or a written action is answered
 * with, and how a moment is written. The words are the language files' (`moderation.*` in `odyssey-lang`).
 */

/**
 * The GM commands the page reads, by their word: each action, `unban` and `unfreeze`, which are lifts that end one
 * kind and have a usage line of their own (ADR-056's amendment of 2026-10-06, decision 11), `actions` and `gm`. The
 * Alert, the action `message`, has no command until P1.4-T105 gives it `/alert`, which is the Flag's second word today.
 */
export type GmCommand = Exclude<ModerationAction, "message"> | "unban" | "unfreeze" | "actions" | "gm";

/**
 * A moment as the page writes it for a player or a GM: the date and the time on the reader's own clock, in the
 * reader's own locale.
 *
 * @param at The moment, in milliseconds since the epoch.
 * @returns The words, as "4 Oct 2026, 14:30" reads in British English.
 */
export function whenWords(at: number): string {
  return new Date(at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * What a banned player is told, on the notice its close shows and on the log in form: until when, or permanently,
 * and why (ADR-056 decision 4).
 *
 * @param reason The reason the GM gave.
 * @param until The ban's end in milliseconds since the epoch, or null for a permanent one.
 * @param when Writes a moment; `whenWords` unless a test gives another.
 * @returns The sentence.
 * @example
 * banSentence("account trading", null);
 * // "You are banned permanently: account trading"
 */
export function banSentence(reason: string, until: number | null, when: (at: number) => string = whenWords): string {
  return until === null ? say("moderation.notice.banPermanent", { reason }) : say("moderation.notice.banUntil", { when: when(until), reason });
}

/**
 * The line that answers a GM whose lift was written: what ended, by its kind, or, when the answer names no kind, as
 * a gateway that does not send `ended` answers, how many actions ended.
 *
 * @param family The family as the gateway holds it.
 * @param count How many actions ended.
 * @param ended The kinds that ended, in any order, or null when the answer named none.
 * @returns The sentence.
 * @example
 * liftSentence("Rook", 2, ["freeze", "ban"]);
 * // "Ended the ban and the freeze on the Rook family."
 */
export function liftSentence(family: string, count: number, ended: readonly LiftableAction[] | null): string {
  const ban = ended?.includes("ban") ?? false;
  const freeze = ended?.includes("freeze") ?? false;
  return ban && freeze ? say("moderation.desk.done.endedBoth", { family }) : ban ? say("moderation.desk.done.endedBan", { family }) : freeze ? say("moderation.desk.done.endedFreeze", { family }) : say("moderation.desk.done.lift", { family, count });
}

/**
 * The line that answers a GM whose action was written.
 *
 * @param answer The action, the family as the gateway holds it, the end or null, how many actions a lift ended and
 *   their kinds, null when the answer named none, and for a freeze the fight it paused: whether the character was in
 *   combat and whether it was a Combatant, each said only when true (the word `unknown` and an absent field say
 *   nothing; ADR-056's amendment "a frozen character is safe", decision 47).
 * @param now The page's clock, which a freeze's time left is counted from.
 * @param when Writes a moment; `whenWords` unless a test gives another.
 * @returns The sentence.
 */
export function doneSentence(answer: { readonly action: ModerationAction; readonly familyName: string; readonly until: number | null; readonly lifted: number; readonly ended: readonly LiftableAction[] | null; readonly inCombat?: boolean | "unknown" | undefined; readonly combatant?: boolean | "unknown" | undefined }, now: number, when: (at: number) => string = whenWords): string {
  const family = answer.familyName;
  switch (answer.action) {
    case "kick":
      return say("moderation.desk.done.kick", { family });
    case "freeze":
      return [say("moderation.desk.done.freeze", { family, time: muteTime(Math.max(0, (answer.until ?? now) - now)) }), ...(answer.inCombat === true ? [say("moderation.desk.done.freezeInCombat")] : []), ...(answer.combatant === true ? [say("moderation.desk.done.freezeCombatant")] : [])].join(" ");
    case "warn":
      return say("moderation.desk.done.warn", { family });
    case "message":
      return say("moderation.desk.done.message", { family });
    case "alert":
      return say("moderation.desk.done.alert", { family });
    case "ban":
      return answer.until === null ? say("moderation.desk.done.banPermanent", { family }) : say("moderation.desk.done.banUntil", { family, when: when(answer.until) });
    case "lift":
      return liftSentence(family, answer.lifted, answer.ended);
    case "feedbackpause":
      return say("moderation.desk.done.feedbackpause", { family, when: when(answer.until ?? now) });
    case "feedbackresume":
      return say("moderation.desk.done.feedbackresume", { family });
    case "feedbackoff":
      return say("moderation.desk.done.feedbackoff", { family });
    case "feedbackon":
      return say("moderation.desk.done.feedbackon", { family });
  }
}

/**
 * A moment as the Moderation window's rows write it, short enough for a column: the time alone today ("14:02"), or
 * "Today 14:02" in a column that names the day, "Yesterday 22:40", and the day and month before that ("6 Oct"), on
 * the reader's own clock and in their locale.
 *
 * @param at The moment, in milliseconds since the epoch.
 * @param now The page's clock.
 * @param namesToday Whether today's moments name the day, as a column of days does.
 * @returns The words.
 */
export function shortWhenWords(at: number, now: number, namesToday = false): string {
  const moment = new Date(at);
  const time = moment.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const day = (when: Date): string => `${when.getFullYear()}-${when.getMonth()}-${when.getDate()}`;
  if (day(moment) === day(new Date(now))) {
    return namesToday ? say("moderation.window.today", { time }) : time;
  }
  if (day(moment) === day(new Date(now - 24 * 60 * 60 * 1000))) {
    return say("moderation.window.yesterday", { time });
  }
  return moment.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * A staff badge in words, as the Moderation window's list and a family's read name it (ADR-056's amendment of
 * 2026-10-06, items 1 and 3): the type, "team leader" for a leader's, a Guide's years of service by the chevrons, and
 * ", hidden" after a badge its person has hidden. A number this page does not know is nothing, as the badge itself
 * is drawn.
 *
 * @param badge The badge number the gateway sent.
 * @param hidden Whether the person has hidden it.
 * @returns The words, or null for no badge and one out of range.
 */
export function badgeWords(badge: number, hidden: boolean): string | null {
  const words = (() => {
    switch (badge) {
      case BADGE.guide1:
        return say("moderation.badge.guide");
      case BADGE.guide2:
      case BADGE.guide3:
      case BADGE.guide4:
        return say("moderation.badge.guideYears", { count: badge - BADGE.guide1 });
      case BADGE.guideLead:
        return say("moderation.badge.guideLead");
      case BADGE.support:
        return say("moderation.badge.support");
      case BADGE.supportLead:
        return say("moderation.badge.supportLead");
      case BADGE.events:
        return say("moderation.badge.events");
      case BADGE.eventsLead:
        return say("moderation.badge.eventsLead");
      case BADGE.builder:
        return say("moderation.badge.builder");
      case BADGE.builderLead:
        return say("moderation.badge.builderLead");
      case BADGE.designer:
        return say("moderation.badge.designer");
      case BADGE.designerLead:
        return say("moderation.badge.designerLead");
      case BADGE.administrator:
        return say("moderation.badge.administrator");
      case BADGE.administratorLead:
        return say("moderation.badge.administratorLead");
      default:
        return null;
    }
  })();
  return words === null || !hidden ? words : say("moderation.badge.hidden", { badge: words });
}
